import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import confirmImage from 'src/assets/imgs/confirmImage.png';
import { sendRequestPendingWallet } from 'src/services/wallet';

export type SendWalletApprovalDialogProps = {
  open: boolean;
  onClose: () => void;
  walletId: string;
  walletName: string;
  onSuccess?: () => void;
  invalidateQueries?: string[][];
};

export function SendWalletApprovalDialog({
  open,
  onClose,
  walletId,
  walletName,
  onSuccess,
  invalidateQueries = [],
}: SendWalletApprovalDialogProps) {
  const queryClient = useQueryClient();

  const {
    mutate: sendRequestPendingWalletMutation,
    isLoading: isLoadingSendRequestPendingWallet,
  } = useMutation({
    mutationFn: (id: string) => sendRequestPendingWallet(id),
    onSuccess: () => {
      toast.success('Solicitação de envio enviada com sucesso');
      onClose();

      // Invalida as queries fornecidas
      invalidateQueries.forEach((queryKey) => {
        queryClient.invalidateQueries(queryKey);
      });

      // Callback customizado de sucesso
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleClose = () => {
    if (!isLoadingSendRequestPendingWallet) {
      onClose();
    }
  };

  const handleConfirm = () => {
    sendRequestPendingWalletMutation(walletId);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-evenly',
          padding: '32px 20px 0',
          overflow: 'hidden',
          minHeight: '465px',
        }}>
        <img src={confirmImage} width={185} height={185} alt="Confirmação" />
        <Typography
          sx={{ padding: '0px 64px' }}
          variant="h4"
          textAlign="center">
          Enviar carteira para aprovação
        </Typography>
        <Typography
          maxWidth={430}
          textAlign="center"
          sx={{ fontSize: '0.875rem' }}>
          Tem certeza que deseja enviar a carteira&nbsp;
          <strong>{walletName}</strong> para aprovação?
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 0 40px 0',
          gap: 2,
        }}
        disableSpacing>
        <Button
          size="large"
          variant="outlined"
          color="secondary"
          onClick={handleClose}
          disabled={isLoadingSendRequestPendingWallet}
          sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Button
          size="large"
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoadingSendRequestPendingWallet}
          sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          {isLoadingSendRequestPendingWallet ? 'Enviando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
